import { Popper, styled } from '@mui/material'
import { useEffect, useState } from 'react'
import { NAV_ITEM_GAP, NavList } from './NavList'
import { SideNavContext } from './SideNavContext'
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react'

/** `keyboard` hands focus back to the group's header; `blur` and `select` leave it where it went. */
export type NavFlyoutCloseReason = 'keyboard' | 'blur' | 'select'

type NavFlyoutProps = {
  id: string
  title: string
  anchorEl: HTMLElement | null
  open: boolean
  /** Moves focus to the first item once open, for a flyout opened from the keyboard. */
  autoFocus: boolean
  onClose: (reason: NavFlyoutCloseReason) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  /** The group's `NavItem`s. */
  children: ReactNode
}

// Same surface as the OmUI Popover. That one is modal, so it can't stay open while the pointer hovers
// the rail beside it. A `nav` of its own, as the portal puts it outside the side nav's landmark.
const Surface = styled('nav')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: NAV_ITEM_GAP,
  width: '15rem',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape['radius-xl'],
  border: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
  boxShadow: theme.customShadows.md,
  backgroundColor: theme.palette.bg.surface.elevated,
}))

const Title = styled('span')(({ theme }) => ({
  ...theme.typography['small-medium'],
  color: theme.palette.text.muted,
  padding: theme.spacing(1.5, 2.5),
}))

// The design puts the flyout 6px from the rail item it opens from, overlapping the rail's edge.
const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 6] } }]
const EXPANDED_ITEMS = { collapsed: false }
const ITEM_SELECTOR = 'a, button'

/**
 * The menu pop-up a collapsed `NavGroup` opens beside the icon rail, listing the group's items by name.
 * It renders in a portal, so the nav's own scrolling and fade mask don't clip it.
 */
export const NavFlyout = ({
  id,
  title,
  anchorEl,
  open,
  autoFocus,
  onClose,
  onMouseEnter,
  onMouseLeave,
  children,
}: NavFlyoutProps) => {
  // Held in state: the Popper's portal mounts the surface a render after `open` turns true.
  const [surface, setSurface] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (autoFocus) surface?.querySelector<HTMLElement>(ITEM_SELECTOR)?.focus()
  }, [surface, autoFocus])

  // The flyout sits in a portal at the end of the page, so tabbing on past its last item would leave the
  // nav entirely. Leaving either end hands focus back to the header, and the next Tab carries on down the nav.
  const handleKeyDown = (event: KeyboardEvent) => {
    const items = [...(surface?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? [])]
    const edge = event.shiftKey ? items.at(0) : items.at(-1)
    if (event.key === 'Escape' || (event.key === 'Tab' && document.activeElement === edge)) {
      event.preventDefault()
      onClose('keyboard')
    }
  }

  const handleBlur = (event: FocusEvent) => {
    if (!surface?.contains(event.relatedTarget)) onClose('blur')
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={POPPER_MODIFIERS}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <Surface
        ref={setSurface}
        id={id}
        aria-label={title}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={() => onClose('select')}
      >
        <Title aria-hidden>{title}</Title>
        <SideNavContext.Provider value={EXPANDED_ITEMS}>
          <NavList>{children}</NavList>
        </SideNavContext.Provider>
      </Surface>
    </Popper>
  )
}
